import React from "react";
import { Row, Col, Button } from "react-bootstrap";
import Link from "next/link";
import { BadgeCheck, Star, Bookmark, MapPin, Users } from "lucide-react";

const CompanyReviews = (props) => {
  const reviews = props.reviews || [];
  const company = props.company || {};

  // Helper to format project duration dates (e.g., YYYY-MM-DD to "MMM. YYYY")
  const formatProjectDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Helper to format publication date (e.g., "Published Jun 5, 2026")
  const formatPublishDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `Published ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Custom Lucide Rating Star Renderer using exact rating color scheme (#43128e) and Lucide icon
  const renderLucideStars = (scoreOutOf10) => {
    const starsArray = [];
    const starsOutOf5 = (scoreOutOf10 || 0) / 2;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= starsOutOf5) {
        // Full brand-colored solid star
        starsArray.push(
          <Star 
            key={i} 
            style={{ width: "16px", height: "16px", color: "#43128e", fill: "#43128e" }} 
          />
        );
      } else if (i - 0.5 === starsOutOf5) {
        // Half brand-colored clipped star (absolutely positioned to prevent scaling)
        starsArray.push(
          <div key={i} style={{ position: "relative", display: "inline-block", width: "16px", height: "16px", verticalAlign: "middle" }}>
            <Star style={{ width: "16px", height: "16px", position: "absolute", left: 0, top: 0, color: "#43128e" }} />
            <div style={{ width: "50%", height: "16px", overflow: "hidden", position: "absolute", left: 0, top: 0 }}>
              <Star style={{ width: "16px", height: "16px", position: "absolute", left: 0, top: 0, color: "#43128e", fill: "#43128e" }} />
            </div>
          </div>
        );
      } else {
        // Empty brand-colored stroke star
        starsArray.push(
          <Star 
            key={i} 
            style={{ width: "16px", height: "16px", color: "#43128e" }} 
          />
        );
      }
    }
    return <div className="d-flex gap-1 align-items-center">{starsArray}</div>;
  };

  // Calculations for Customer Reviews Summary Block
  const totalReviewsCount = reviews.length;

  let avgOverall10 = totalReviewsCount > 0
    ? Number((reviews.reduce((sum, r) => sum + (r.ratings?.overallSatisfaction || 0), 0) / totalReviewsCount).toFixed(1))
    : 0;

  const getAverage = (key) => {
    const validReviews = reviews.filter(r => r.ratings && r.ratings[key] !== undefined);
    if (validReviews.length === 0) return 0;
    const sum = validReviews.reduce((acc, r) => acc + Number(r.ratings[key]), 0);
    return Number((sum / validReviews.length).toFixed(1));
  };

  const ratingKeys = [
    { key: "qualityOfServices", label: "Service quality" },
    { key: "adherenceToDeadlines", label: "Meeting deadlines" },
    { key: "communicationCollaboration", label: "Communication" },
    { key: "problemSolvingFlexibility", label: "Problem solving" },
    { key: "valueForMoney", label: "Value for money" },
    { key: "likelihoodToRecommend", label: "Would recommend" }
  ];

  return (
    <div id="reviews" className="text-block mt-5 pt-0">
      {/* Dynamic responsive styles to enforce the precise column proportions on desktop */}
      <style dangerouslySetInnerHTML={{__html: `
        @media (min-width: 992px) {
          .col-reviewer {
            flex: 0 0 18% !important;
            max-width: 18% !important;
          }
          .col-project {
            flex: 0 0 24% !important;
            max-width: 24% !important;
          }
          .col-review {
            flex: 0 0 58% !important;
            max-width: 58% !important;
          }
        }
      `}} />

      {/* 📊 Customer Reviews Summary Block */}
      {totalReviewsCount > 0 && (
        <React.Fragment>
          <div className="bg-white pb-5 border-bottom text-dark" style={{ fontFamily: "'Inter', sans-serif" }}>
            <Row className="g-4 align-items-center">
              
              {/* Left Block: Customer Reviews Rating */}
              <Col md="4" className="d-flex flex-column justify-content-center pe-md-4">
                <h2 className="h4 fw-medium mb-2" style={{ color: "#212529" }}>Customer Reviews</h2>
                <div className="d-flex align-items-center gap-3 mt-2">
                  <div className="d-flex align-items-baseline gap-1">
                    <span className="fw-bold text-dark" style={{ fontSize: "36px", lineHeight: "1" }}>{avgOverall10}</span>
                    <span className="text-muted" style={{ fontSize: "16px" }}>/ 10</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {renderLucideStars(avgOverall10)}
                    <span className="text-muted text-sm fw-normal" style={{ fontSize: "14px" }}>
                      ({totalReviewsCount})
                    </span>
                  </div>
                </div>
              </Col>

              {/* Right Block: Averages by Parameter */}
              <Col md="8" className="ps-md-4">
                <div className="d-flex flex-column gap-2">
                  {ratingKeys.map(({ key, label }) => {
                    const avg = getAverage(key);
                    const percent = avg * 10;
                    return (
                      <div key={key} className="d-flex align-items-center justify-content-between gap-3 py-1">
                        {/* Parameter Label & Value */}
                        <div className="d-flex align-items-center" style={{ width: "150px", flexShrink: 0 }}>
                          <span className="text-dark fw-medium text-nowrap" style={{ fontSize: "13px" }}>
                            {label}
                          </span>
                          <span className="fw-bold text-dark ms-auto pe-2" style={{ fontSize: "13px" }}>
                            {avg.toFixed(1)}
                          </span>
                        </div>
                        
                        {/* Progress Bar Container */}
                        <div className="flex-grow-1 bg-light rounded-pill overflow-hidden" style={{ height: "8px", position: "relative" }}>
                          <div 
                            className="rounded-pill"
                            style={{ 
                              height: "100%", 
                              width: `${percent}%`, 
                              backgroundColor: "#8980ff", 
                              transition: "width 0.3s ease" 
                            }} 
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Col>

            </Row>
          </div>

          {company.company_name && (
            <div className="d-flex justify-content-end mt-3 mb-3">
              <Button 
                variant="outline-primary" 
                size="sm" 
                href={`/review/add?company=${encodeURIComponent(company.company_name)}`}
              >
                Write a review
              </Button>
            </div>
          )}
        </React.Fragment>
      )}

      <div className="d-flex flex-column gap-5">
        {reviews.map((review, index) => {
          const reviewer = review.reviewer_info || {};
          const identification = reviewer.identification || {};
          
          // Privacy check for name & company disclosure
          const showName = identification.value === 'show_both' || identification.value === 'show_name';
          const showCompany = identification.value === 'show_both' || identification.value === 'show_company';
          
          const reviewerCompany = showCompany ? reviewer.companyName : "";
          const reviewerLastName = reviewer.lastName ? ` ${reviewer.lastName}` : "";
          const reviewerName = showName ? `${reviewer.firstName}${reviewerLastName}` : "Anonymous";
          
          const initials = (
            (reviewer.firstName ? reviewer.firstName.charAt(0) : "") +
            (reviewer.lastName ? reviewer.lastName.charAt(0) : "")
          ).toUpperCase() || "A";
          
          const project = review.project_info || {};
          const duration = project.projectStartDate && project.projectEndDate
            ? `${formatProjectDate(project.projectStartDate)} - ${formatProjectDate(project.projectEndDate)}`
            : "";

          // Verified Badge holds true for real client email submissions (not admin-entered reviews)
          const isVerified = (review.verification_status === 'verified' || reviewer.verificationStatus === 'verified') && review.corporate_email;
          const ratings = review.ratings || {};
          const feedback = review.feedback || {};

          return (
            <React.Fragment key={review.review_id}>
              <div className="review-card bg-white rounded-3 text-dark font-sans" style={{ fontFamily: "'Inter', sans-serif" }}>
              {/* Header Bar: Shown ONLY if an admin title exists, otherwise omitted completely */}
              {review.admin_title && (
                <div className="pe-4 pt-0 pb-0 rounded-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h4 className="fw-medium mb-0" style={{ fontSize: "1.25rem", letterSpacing: "0.25px", color: "#212529" }}>
                    {review.admin_title}
                  </h4>
                  {review.created_at && (
                    <span className="text-muted text-sm fw-normal" style={{ fontSize: "13px" }}>
                      {formatPublishDate(review.created_at)}
                    </span>
                  )}
                </div>
              )}

              {/* Four-Column Grid Content Section */}
              <div className="p-4">
                <Row className="g-4">
                  
                  {/* 👤 COLUMN 1: The Reviewer (Client Profile) - 18% Width on Desktop */}
                  <Col lg="3" className="col-reviewer pe-lg-3 border-end-lg">
                    <h3 className="text-uppercase fw-medium mb-3 tracking-wider" style={{ fontSize: "12.8px", letterSpacing: "1px", color: "#212529" }}>
                      THE REVIEWER
                    </h3>
                    <div className="d-flex flex-column gap-2">
                      
                      {/* Shadcn Block Reviews 2 Avatar Row */}
                      <div className="d-flex align-items-center gap-3 mb-2">
                        {/* Initials Avatar Circle (e.g. MT or A) */}
                        <div className="rounded-circle d-flex align-items-center justify-content-center bg-light border fw-semibold text-muted" style={{ width: "40px", height: "40px", fontSize: "13px", letterSpacing: "0.5px", flexShrink: 0 }}>
                          {showName ? initials : "A"}
                        </div>
                        {/* Name & Occupation */}
                        <div className="d-flex flex-column" style={{ lineHeight: 1.2 }}>
                          <span className="fw-medium" style={{ fontSize: "14px", lineHeight: "20px", color: "#212529" }}>
                            {reviewerName}
                          </span>
                          {(reviewer.jobTitle || reviewerCompany) && (
                            <span className="text-muted" style={{ fontSize: "12px", lineHeight: "16px" }}>
                              {reviewer.jobTitle}
                              {reviewer.jobTitle && reviewerCompany ? " at " : ""}
                              {reviewerCompany}
                            </span>
                          )}
                        </div>
                      </div>

                      {reviewer.industry?.label && (
                        <div className="text-muted text-sm d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                          <span className="custom-tooltip-wrapper" data-tooltip="Industry">
                            <Bookmark className="text-muted" style={{ width: "14px", height: "14px", flexShrink: 0 }} />
                          </span>
                          <span>{reviewer.industry.label}</span>
                        </div>
                      )}
                      {reviewer.companyHeadquarters && (
                        <div className="text-muted text-sm d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                          <span className="custom-tooltip-wrapper" data-tooltip="Location">
                            <MapPin className="text-muted" style={{ width: "14px", height: "14px", flexShrink: 0 }} />
                          </span>
                          <span>{reviewer.companyHeadquarters}</span>
                        </div>
                      )}
                      {reviewer.companySize?.label && (
                        <div className="text-muted text-sm d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                          <span className="custom-tooltip-wrapper" data-tooltip="Client size">
                            <Users className="text-muted" style={{ width: "14px", height: "14px", flexShrink: 0 }} />
                          </span>
                          <span>{reviewer.companySize.label}</span>
                        </div>
                      )}
                      {isVerified && (
                        <div className="mt-2">
                          <span className="d-inline-flex align-items-center text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.5px", color: "#009966" }}>
                            <BadgeCheck className="me-1" style={{ width: "12px", height: "12px" }} />
                            Verified
                          </span>
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* 💼 COLUMN 2: The Project (Scope & Details) - 24% Width on Desktop */}
                  <Col lg="3" className="col-project px-lg-3 border-end-lg">
                    <h3 className="text-uppercase fw-medium mb-3 tracking-wider" style={{ fontSize: "12.8px", letterSpacing: "1px", color: "#212529" }}>
                      THE PROJECT
                    </h3>
                    <div className="d-flex flex-column gap-3">
                      {/* Services Provided */}
                      {project.deliveredServices && project.deliveredServices.length > 0 && (
                        <div>
                          <div className="label-heading">
                            Services Provided
                          </div>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {project.deliveredServices.map((service) => (
                              <span key={service} className="focus-area-link" style={{ fontSize: "11px", padding: "0.3rem 0.75rem" }}>
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Budget */}
                      {project.projectBudget?.label && (
                        <div>
                          <div className="label-heading">
                            Project Budget
                          </div>
                          <div className="text-dark" style={{ fontSize: "13px", fontWeight: 500 }}>
                            {project.keepBudgetConfidential ? "Confidential" : project.projectBudget.label}
                          </div>
                        </div>
                      )}

                      {/* Duration */}
                      {duration && (
                        <div>
                          <div className="label-heading">
                            Project Duration
                          </div>
                          <div className="text-dark" style={{ fontSize: "13px", fontWeight: 500 }}>
                            {duration}
                          </div>
                        </div>
                      )}

                      {/* Project Overview */}
                      {project.projectOverview && (
                        <div>
                          <div className="label-heading">
                            Project Overview
                          </div>
                          <div className="text-muted text-sm lh-base" style={{ fontSize: "13px" }}>
                            {project.projectOverview}
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* 💬 COLUMN 3: The Review (Ratings & Feedback) - 58% Width on Desktop */}
                  <Col lg="6" className="col-review ps-lg-3">
                    <h3 className="text-uppercase fw-medium mb-3 tracking-wider" style={{ fontSize: "12.8px", letterSpacing: "1px", color: "#212529" }}>
                      THE REVIEW
                    </h3>
                    <Row className="g-4">
                      {/* Left side: Ratings (KPI breakdown) */}
                      <Col md="3">
                        <div className="d-flex flex-column gap-3">
                          {/* Overall Satisfaction */}
                          <div className="d-flex flex-column gap-1">
                            <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                              Overall: {ratings.overallSatisfaction || 0}/10
                            </span>
                            <div className="d-inline-flex">
                              {renderLucideStars(ratings.overallSatisfaction || 0)}
                            </div>
                          </div>

                          {/* Granular Metrics (B&W) */}
                          <div className="d-flex flex-column gap-2 text-sm">
                            {ratings.qualityOfServices !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Service quality</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.qualityOfServices}</span>
                              </div>
                            )}
                            {ratings.adherenceToDeadlines !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Meeting deadlines</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.adherenceToDeadlines}</span>
                              </div>
                            )}
                            {ratings.communicationCollaboration !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Communication</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.communicationCollaboration}</span>
                              </div>
                            )}
                            {ratings.problemSolvingFlexibility !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Problem solving</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.problemSolvingFlexibility}</span>
                              </div>
                            )}
                            {ratings.valueForMoney !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Value for money</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.valueForMoney}</span>
                              </div>
                            )}
                            {ratings.likelihoodToRecommend !== undefined && (
                              <div className="d-flex justify-content-between py-1 border-bottom">
                                <span className="text-muted" style={{ fontSize: "12px" }}>Would recommend</span>
                                <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{ratings.likelihoodToRecommend}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>

                      {/* Right side: Feedback (Written comments) */}
                      <Col md="9">
                        <div className="d-flex flex-column gap-3">
                          
                          {/* What the client liked */}
                          {feedback.extraFeedbackLike && (
                            <div>
                              <div className="label-heading">
                                What the client liked
                              </div>
                              <p className="text-muted lh-base mb-2 italic" style={{ fontSize: "12.5px", fontStyle: "italic" }}>
                                "{feedback.extraFeedbackLike}"
                              </p>
                              {feedback.prosText && feedback.prosText.length > 0 && (
                                <div className="d-flex flex-wrap gap-1">
                                  {feedback.prosText.map((pro) => (
                                    <span key={pro} className="focus-area-link focus-area-pro" style={{ fontSize: "11px", padding: "0.3rem 0.75rem" }}>
                                      {pro}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Areas for improvement */}
                          {feedback.extraFeedbackDislike && (
                            <div>
                              <div className="label-heading">
                                Areas for improvement
                              </div>
                              <p className="text-muted lh-base mb-2" style={{ fontSize: "12.5px", fontStyle: "italic" }}>
                                "{feedback.extraFeedbackDislike}"
                              </p>
                              {feedback.consText && feedback.consText.length > 0 && (
                                <div className="d-flex flex-wrap gap-1">
                                  {feedback.consText.map((con) => (
                                    <span key={con} className="focus-area-link focus-area-con" style={{ fontSize: "11px", padding: "0.3rem 0.75rem" }}>
                                      {con}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                        </div>
                      </Col>
                    </Row>
                  </Col>

                </Row>
              </div>

            </div>
            {index < reviews.length - 1 && (
              <hr className="my-0" style={{ borderTop: "1px solid #e9ecef", opacity: 1 }} />
            )}
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
};

export default CompanyReviews;
